import React, {
  lazy,
  Suspense,
  type FC,
  type ReactNode,
  useEffect,
  useReducer,
  useState,
  type PropsWithChildren,
} from 'react';

import styled from '@emotion/styled';
import type { FeatureFlag } from 'generated/drupal-graphql';
import { isBrowserIE, useSiteMetadata, getI18nData, getLanguage, formatLanguageKey, isBrowser } from '@lucid/utils';
import { graphql, useStaticQuery } from 'gatsby';
import {
  AlertBanner,
  AnnouncementBanner,
  Box,
  LayoutContext,
  Pingdom,
  type SiteFooterOptionalProps,
  type SiteFooterSocialLinks,
  SiteFooter,
  GlobalCSS,
  ThemeProvider,
  ABTest,
} from '@lucid/components-react';
import { theme } from '@lucid/theme';
import Navbar, { type NavbarOptionalProps } from 'components/navbar/navbar';
import { useMenuFooter } from 'hooks/use-menu';
import type { Template, I18n } from 'types/types';

const CMSNodeButton = lazy(() => import('@lucid/components-react').then(module => ({ default: module.CMSNodeButton })));
const ABTestAdminUi = lazy(() => import('@lucid/components-react').then(module => ({ default: module.ABTestAdminUi })));

interface ABTestAction {
  [key: string]: string;
}

interface ABTestObj {
  [key: string]: ABTestAction;
}

interface StaticQuery {
  allFeatureFlag: {
    nodes: FeatureFlag[];
  };
  allFooterSocialIconsJson: {
    edges: SiteFooterSocialLinks[];
  };
  i18nData: I18n.DataEdges;
}

interface LayoutProps extends Template.LayoutProps {
  abTestTemplateSectionLink?: string | null;
  children: ReactNode;
  navbarProps?: NavbarOptionalProps;
  footerProps?: SiteFooterOptionalProps;
  hasNavbar?: boolean | null;
  hasFooter?: boolean | null;
}

const EmotionLayoutContextProvider = styled(LayoutContext.Provider)({});

const Layout: FC<PropsWithChildren<LayoutProps>> = ({
  shortLangPrefix,
  pageType,
  children,
  shouldHideSsr = false,
  entityTranslations = [],
  metatags = [],
  nid = '',
  navbarProps,
  footerProps,
  hasNavbar = true,
  hasFooter = true,
}) => {
  const { i18nData } = useStaticQuery<StaticQuery>(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_nav_mobile_toggle
                mktg_nav_mobile_toggle_suite
                mktg_footer_about_privacy
                mktg_footer_legal
                mktg_footer_cookie_preferences
                mktg_footer_language_switch
                mktg_nav_aria_label
                mktg_top_nav_aria_label
                mktg_nav_lang
                mktg_footer_nav_lang
                mktg_footer_app_download_alt
              }
            }
          }
        }
      }
    `,
  );

  const { cmsUrl, product } = useSiteMetadata();
  const [shouldShowShield, setShouldShowShield] = useState(shouldHideSsr);
  const [shouldShowEditButton, setShouldShowEditButton] = useState(false);
  const [shouldShowABTestButtons, setShouldShowABTestButtons] = useState(false);
  const { i18nKey, urlLangPrefix, languageKey } = getLanguage(shortLangPrefix);
  const currentLangName = formatLanguageKey(shortLangPrefix, 'name');
  const i18n = getI18nData(i18nData, i18nKey);
  const [abTests, updateTest] = useReducer((state: ABTestObj, action: ABTestAction) => {
    const currentABTestPropKeys = Object.keys(state);

    if (!currentABTestPropKeys.includes(action.testName)) {
      return {
        ...state,
        [action.testName]: action,
      };
    }

    if (currentABTestPropKeys.includes(action.testName)) {
      return {
        ...state,
        [action.testName]: {
          ...state[action.testName],
          ...action,
        },
      };
    }

    return state;
  }, {});

  const applicationLinks = [
    {
      url: 'https://itunes.apple.com/us/app/lucidchart-flowchart-diagram-visio-viewer/id611543423?mt=8',
      image: 'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/footer/app-store-white.svg',
      alt: i18n('mktg-footer-app-download-alt', { platform: 'Apple App Store' }) || 'Download on the Apple App Store',
      detail: 'app-store',
    },
    {
      url: 'https://play.google.com/store/apps/details?id=com.lucidchart.android.chart&amp;hl=en',
      image: 'https://cdn-cashy-static-assets.lucidchart.com/marketing/pages/chart/Footer/google-play.png',
      alt:
        i18n('mktg-footer-app-download-alt', {
          platform: 'Google Play Store',
        }) || 'Download on the Google Play Store',
      detail: 'google-play',
    },
  ];

  useEffect(() => {
    if (isBrowser()) {
      setShouldShowEditButton(window.localStorage.getItem('showEditLink') === '1');
      setShouldShowABTestButtons(window.localStorage.getItem('showAbTestingUi') === '1');
    }
  }, []);

  useEffect(() => {
    const mobileThreshold = 768;
    const mobileTimeout = 4000;
    const desktopTimeout = 1000;
    const timeoutInMs = window.innerWidth >= mobileThreshold ? desktopTimeout : mobileTimeout;

    const safetyTimeoutId = setTimeout(() => {
      setShouldShowShield(false);
    }, timeoutInMs);

    return () => {
      clearTimeout(safetyTimeoutId);
    };
  }, [shouldHideSsr]);

  const layoutContext = {
    language: {
      languageKey,
      shortLangPrefix,
      urlLangPrefix,
      i18nKey,
    },
    pageType,
    product,
    abTesting: {
      updateTest,
    },
    metatags,
  };
  // Footer props
  const footerMenu = useMenuFooter(languageKey);

  return (
    <EmotionLayoutContextProvider value={layoutContext}>
      <GlobalCSS />
      <Pingdom />
      <ThemeProvider product={product} theme={theme}>
        <ABTest.Shield shouldShowShield={shouldShowShield} />
        {hasNavbar && (
          <Navbar
            {...navbarProps}
            buttonAriaLabel={i18n('mktg-footer-language-switch', {
              language: currentLangName,
            })}
            entityTranslations={entityTranslations}
            i18nData={i18nData}
            languageKey={languageKey}
            logo={{
              imageSrc: 'https://cdn-cashy-static-assets.lucidchart.com/lucid/logos/lucidchart.svg',
              imageAlt: 'Go to Lucidchart homepage',
            }}
            nid={nid}
            pageType={pageType}
          />
        )}
        <Box as="main" className="gatsby-root" data-lang={shortLangPrefix} data-nid={nid} data-page-type={pageType}>
          <AnnouncementBanner product={product} />
          {children}
          <AlertBanner
            hasCondition={isBrowserIE()}
            linkProps={{
              to: 'https://lucid.co/old_browser',
              label: 'Read More',
              underline: 'always',
            }}
            storageKey="ie11-unsupported"
            variant="alert"
          >
            <strong>Lucidchart no longer supports Internet Explorer 11.</strong>
            &nbsp; Click the link to learn how to upgrade to a supported browser.
          </AlertBanner>
          <Suspense fallback={<div key="cmsNodeSuspenseFallback" id="cmsnode-suspense-fallback" />}>
            {shouldShowEditButton && <CMSNodeButton href={`${cmsUrl}/${urlLangPrefix}node/${nid}/edit`} />}
          </Suspense>
          <Suspense fallback={<div key="abTestsSuspenseFallback" id="abtests-suspense-fallback" />}>
            {shouldShowABTestButtons && !!Object.keys(abTests).length && <ABTestAdminUi abTests={abTests as any} />}
          </Suspense>
        </Box>
        {hasFooter && (
          <SiteFooter
            {...footerProps}
            applicationLinks={applicationLinks}
            entityTranslations={entityTranslations}
            i18nData={i18nData}
            menu={footerMenu}
          />
        )}
      </ThemeProvider>
    </EmotionLayoutContextProvider>
  );
};

export default Layout;

export const query = graphql`
  fragment link on Drupal_ParagraphLink {
    fieldOpenNewTab
    fieldBeaconElementDetail
    fieldCta {
      url {
        ...getLinkPath
      }
      title
    }
  }
  fragment extraTranslations on Drupal_ParagraphExtraTranslations {
    fieldTranslationNode {
      entity {
        ...entityTranslations
      }
    }
    fieldPreferReferenceTranslati
  }
  fragment marketo on Drupal_ParagraphMarketoForm {
    entityLanguage {
      id
    }
    fieldMarketoId
    fieldMarketoTitle
    fieldMarketoBody {
      processed
    }
    fieldMarketoNote {
      processed
    }
    fieldMarketoCtaText
  }
  fragment menuFragment on Drupal_MenuLinkContent {
    menuName
    parent
    entityUuid
    entityLanguage {
      id
    }
    title
    link {
      url {
        path
      }
    }
    ... on Drupal_MenuLinkContentFooter {
      fieldBeaconElementDetail
      fieldKey
      fieldLinkOverride
    }
    ... on Drupal_MenuLinkContentMoreExamplesList {
      fieldTranslatedLink
      fieldCategoryImage
    }
  }
`;
